/**
 * Utility functions for the Institution Dashboard
 *
 * Re-exports shared utilities from centralized location for backward compatibility.
 */

import type { HistoricalPeriod, QuarterAverages, SankeyIndicator, CAMELCode } from './types';
import { monthNames, quarterEndMonths, categoryMapping } from './constants';

// Re-export rating utilities from shared location
export {
  getRatingIndex,
  getRatingColor,
  getRatingHexColor,
  getScoreColor,
} from '../../utils/ratingColors';

/**
 * Format a period date string (e.g., "2025-05") to display format (e.g., "May 2025")
 */
export function formatPeriodDate(dateStr: string): string {
  const [year, month] = dateStr.split('-');
  const monthIndex = parseInt(month) - 1;
  return `${monthNames[monthIndex]} ${year}`;
}

/**
 * Format a full date string for display (e.g., "2025-05-28" to "28 May 2025")
 */
export function formatFullDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Check if a date is a quarter ending month
 */
export function isQuarterEndMonth(dateStr: string): boolean {
  const [, month] = dateStr.split('-');
  return quarterEndMonths.includes(parseInt(month));
}

/**
 * Get the quarter label for a date (e.g., "Q2 2025")
 */
export function getQuarterLabel(dateStr: string): string {
  const [year, month] = dateStr.split('-');
  const quarter = Math.ceil(parseInt(month) / 3);
  return `Q${quarter} ${year}`;
}

/**
 * Calculate quarter averages for a given starting index in history
 * Used for comparison with previous quarter
 */
export function calculateQuarterAverages(
  history: HistoricalPeriod[],
  startIndex: number
): QuarterAverages {
  // Get 3 periods starting from startIndex (current quarter approximation)
  const periods = history.slice(startIndex, startIndex + 3);

  if (periods.length === 0) {
    return { finalScore: 0, camel: {} };
  }

  // Calculate average final score
  const avgFinalScore = periods.reduce((sum, h) => sum + h.finalScore, 0) / periods.length;

  // Calculate average for each CAMEL component
  const camelAvgs: Record<string, number> = {};

  if (periods[0]?.camel) {
    periods[0].camel.forEach((c) => {
      const sum = periods.reduce((acc, h) => {
        const found = h.camel?.find((hc) => hc.code === c.code);
        return acc + (found ? found.score : 0);
      }, 0);
      camelAvgs[c.code] = sum / periods.length;
    });
  }

  return { finalScore: avgFinalScore, camel: camelAvgs };
}

/**
 * Group indicators by their CAMEL category
 */
export function groupIndicatorsByCategory(
  indicators: SankeyIndicator[]
): Record<string, SankeyIndicator[]> {
  const grouped: Record<string, SankeyIndicator[]> = {};

  indicators.forEach((ind) => {
    const cat = ind.indicator_category;
    if (!grouped[cat]) {
      grouped[cat] = [];
    }
    grouped[cat].push(ind);
  });

  return grouped;
}

/**
 * Get indicators for a specific CAMEL code
 */
export function getIndicatorsForCamelCode(
  indicators: SankeyIndicator[],
  code: CAMELCode
): SankeyIndicator[] {
  const categoryName = categoryMapping[code];
  return indicators.filter((ind) => ind.indicator_category === categoryName);
}

/**
 * Calculate delta between two values and return formatted string
 */
export function formatDelta(current: number, previous: number): string {
  const delta = current - previous;
  const sign = delta >= 0 ? '+' : '';
  return `${sign}${delta.toFixed(2)}`;
}

/**
 * Determine trend direction based on score change
 */
export function getTrendDirection(
  current: number,
  previous: number,
  threshold: number = 0.5
): 'up' | 'down' | 'stable' {
  const delta = current - previous;
  if (delta > threshold) return 'up';
  if (delta < -threshold) return 'down';
  return 'stable';
}

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Calculate percentage position for gauge display
 */
export function calculatePercentage(value: number, min: number, max: number): number {
  return ((value - min) / (max - min)) * 100;
}
